import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { queryClient } from "../lib/queryClient";
import type { Device } from "../lib/types";
import { getDevices } from "../api/axios";
import PerformanceCharts from "../components/dashboard/performance-charts";
import { RefreshCw } from "lucide-react";

export default function Performance() {
  const [timeRange, setTimeRange] = useState("last24hours");
  const [deviceFilter, setDeviceFilter] = useState<string>("all");

  const { data: devices } = useQuery<Device[]>({
    queryKey: ["devices"],
    queryFn: getDevices,
  });

  useEffect(() => {
    // Set up WebSocket event listener for real-time updates
    const handleWsMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;

      if (data.type === "performance_update") {
        // Invalidate Influx-backed multi-device overview
        queryClient.invalidateQueries({ queryKey: ["influx-multi-device"] });
      }
    };

    window.addEventListener("ws-message", handleWsMessage);

    return () => {
      window.removeEventListener("ws-message", handleWsMessage);
    };
  }, []);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["influx-multi-device"] });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">
              Performance Monitoring
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor and analyze system performance metrics from InfluxDB
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-full sm:w-40 mb-2 sm:mb-0">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last24hours">Last 24 hours</SelectItem>
                <SelectItem value="last7days">Last 7 days</SelectItem>
                <SelectItem value="last30days">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Device filter and charts */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <p className="text-sm font-medium text-neutral-dark">Device filter</p>
              <p className="text-xs text-gray-500">
                Select a device to focus charts on that system, or view all devices.
              </p>
            </div>
            <div className="w-full md:w-64">
              <Select
                value={deviceFilter}
                onValueChange={setDeviceFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All devices</SelectItem>
                  {(devices || []).map((device) => {
                    const id = (device as any)._id || (device as any).id;
                    if (!id) return null;
                    return (
                      <SelectItem key={id.toString()} value={id.toString()}>
                        {device.name} {device.ipAddress ? `(${device.ipAddress})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <PerformanceCharts
            timeRange={timeRange}
            selectedDeviceId={deviceFilter === "all" ? undefined : deviceFilter}
          />
        </CardContent>
      </Card>
    </div>
  );
}